import { ChangeDetectionStrategy, Component, Optional, input, signal } from '@angular/core';
import { BookingsStore, UserBookingsStore } from '@fry/booking/lib/bookings';
import { BookingOptionsStore } from '@fry/booking/lib/booking-items/booking-options.store';
import { catchError, map, switchMap } from 'rxjs/operators';
import { EasTable, Model } from '@fry/lib/store';
import { Router, RouterModule } from '@angular/router';
import { UserService } from '@fry/lib/users';
import { ProfileStore } from '@fry/lib/profile';
import { from, Observable, of, tap } from 'rxjs';
import { SecurityService } from '@fry/lib/security';
import { BoardCard } from '@fry/boards/lib';
import { BookingModule } from '@fry/booking/app';
import { CommonModule } from '@angular/common';
import { UnboxedModule } from '@fry/ui';
import { FryCommonModule } from '@fry/components/common';
import { MatButtonModule } from '@angular/material/button';
import { FixedDataTableModule } from '@fry/components/common/fixed-data-table/fixed-data-table.module';
import { InteractiveTableOptions } from '@fry/components/common/list/interactive-table/interactive-table.component';

@Component({
    selector: 'eas-user-available-card',
    templateUrl: './user-available-card.view.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        UnboxedModule,
        FryCommonModule,
        BookingModule,
        MatButtonModule,
        RouterModule,
        FixedDataTableModule,
    ],
    providers: []
})
export class UserAvailableCardViewComponent {
  card = input.required<BoardCard>();

  static key = 'UserAvailableCardComponent';

  public title: string = '';
  public subtitle: string = '';
  public table: EasTable | undefined;
  public $loaded = signal<boolean>(false);
  public defaultColumns = ['bookingItem', 'bookingDates', 'bookingWindow', 'price', 'apply'];
  public options: Partial<InteractiveTableOptions> = {
    enableColumns: false,
    enableControls: false,
  };

  constructor(
    private store: BookingOptionsStore,
    private router: Router,
    private profile: ProfileStore,
    private security: SecurityService,
    private bookings: UserBookingsStore,
    private allbookings: BookingsStore,
    @Optional() private userService: UserService,
  ) {

  }

  ngOnInit(): void {
    this.title = this.card().options.title || $localize `Available bookings`;
    this.subtitle = this.card().options.subtitle || '';

    this.cardFullUsername().pipe(
      switchMap(username => {
        const params = {
          user: username,
          eligible: true,
          path: []
        };
        this.store.reset();
        return this.store.search(params, { strict: true });
      }),
      map(result => {
        const data = result.data.sort((a, b) => a.doc.bookingItem.name.localeCompare(b.doc.bookingItem.name));
        this.table = this.createTable(data);
      }),
      catchError(err => {
        console.error(err);
        return of({columns: [], data: []});
      }),
      tap(() => {
        this.$loaded.set(true);
      }),
    ).subscribe();
  }

  createTable(items: Model[]): EasTable {
    const username = this.cardUserUsername();
    const data = items.map(item => {
      return {
          id: item.doc.id,
          obj: item.doc,
          row: {
            bookingItem: item.doc.bookingItem.name,
            bookingDates: item.doc.summary.bookingDates,
            bookingWindow: item.doc.summary.bookingWindow,
            price: item.doc.summary.price,
            apply: {
              label: item.doc.bookableType === 'cover' ? $localize `View` : $localize `Apply`
            }
          }
      };
    });
    return {
      columns: [
        {
          id: 'bookingItem',
          label: $localize `Booking item`
        },
        {
          id: 'bookingDates',
          label: $localize `Date`,
          type: 'dateRange',
        },
        {
          id: 'bookingWindow',
          label: $localize `Booking window`,
          type: 'dateRange',
        },
        {
          id: 'price',
          label: $localize `Price`,
          type: 'priceRange',
        },
        {
          id: 'apply',
          label: $localize `Action`,
          type: 'button',
          onClick: (row) => {
            if (row.obj.bookableType === 'cover') {
              const url = username === 'profile' ? '/bookings/available' : `/users/${username}/available`;
              return from(this.router.navigate([url], { queryParams: { path: row.id }}));
            }
            const bookingItem = row.obj.bookingItem._id;
            const bookingOption = row.obj.id;

            const apply$ = username !== 'profile' ?
              this.allbookings.apply(username, bookingItem, bookingOption, {}) :
              this.bookings.apply(bookingItem, bookingOption, {});

            return apply$
              .pipe(
                switchMap(result => {
                  return from(this.router.navigate(['bookings',
                                                    result.result.id,
                                                    'edit']));
                })
              );
          }
        }
      ],
      data
    };
  }

  private cardFullUsername(): Observable<string> {
    const username = this.cardUserUsername();
    if (username === 'profile') {
      return this.profile.getCurrent().pipe(map(current => current.doc.user));
    }

    return of(username);
  }

  public cardUserUsername(): string {
    if (this.userService) {
      return this.userService.user.doc.username;
    }

    return 'profile';
  }

  isVisible(): Observable<boolean> {
    return this.cardFullUsername().pipe(
      switchMap(username => {
        return this.security.hasOwnPotentialPermissionFor('bookings.view', username);
      })
    );
  }


}
